/* eslint-disable @typescript-eslint/member-ordering */
import { ID } from '@datorama/akita';
import { Column, Entity } from '../entity';
import { Link } from '../interfaces/helpers';
import { IEntity } from '../interfaces/models';
import { ColumnType, LanguagePage, PageTitles } from '../interfaces/types';
import { VendorSalesRep } from './vendorSalesRep.model';

export enum KnockType {
  drop = 'Drop Knock',
  sales = 'Sales Knock',
}

export interface IBaseKnock extends IEntity {
  knockType: string;
  knockStatus: string;
  addressId: string;
  createdBy?: string;
  createdAt: Date;
  modifiedBy?: string;
  modifiedAt?: Date;
  comments: string;
}

export interface IKnock extends IBaseKnock {
  babylonInteraction: string;
  comebackDate: Date;
  comebackTime: string;
  contractExpiryDate: Date;
  currentInternetProvider: string;
  currentInternetSpeed: string;
  currentMobilityProvider: string;
  endGeolocation: string;
  followUpDate: Date;
  href?: string;
  is6OrMoreSuitesInBuilding: boolean;
  isBuildingInStrata: boolean;
  isThereMainElectricalRoom: boolean;
  isUnderConstruction: boolean;
  languagePreference: string;
  offerExpiryDate: Date;
  orderId: string;
  orderingSystem: string;
  osm: string;
  otherReason: string;
  reasonForNo: string;
  salesActivity: string;
  startGeolocation: string;
  storeCode: string;
  vendorSalesRep: string;
}

interface IExtraKnockInformation {
  createdByData: VendorSalesRep;
  modifiedByData: VendorSalesRep;
  salesRepData: VendorSalesRep;
}

@Entity({
  name: PageTitles.knockTableView,
})
export class BaseKnock implements IBaseKnock {
  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 1,
    },
  })
  knockType: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 2,
    },
  })
  knockStatus: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 3,
    },
  })
  createdAt: Date;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 4,
    },
  })
  comments: string;

  id: ID;
  href?: string;
  addressId: string;
  createdBy?: string;
  modifiedBy?: string;
  modifiedAt?: Date;

  addressLink?: Link;
  createdByLink?: Link;
  modifiedByLink?: Link;
  salesRepLink?: Link;

  constructor(
    data: BaseKnock = {
      addressId: '',
      comments: '',
      createdAt: new Date(),
      modifiedAt: new Date(),
      id: '',
      knockStatus: '',
      knockType: '',
      addressLink: null,
      createdByLink: null,
      modifiedByLink: null,
      salesRepLink: null,
    }
  ) {
    this.addressId = data?.addressId;
    this.knockStatus = data?.knockStatus;
    this.comments = data?.comments;
    this.createdAt = data?.createdAt;
    this.modifiedAt = data?.modifiedAt;
    this.id = data?.id;
    this.knockType = data?.knockType;
    this.createdByLink = data?.createdByLink;
    this.modifiedByLink = data?.modifiedByLink;
    this.addressLink = data?.addressLink;
    this.salesRepLink = data?.salesRepLink;
  }
}

@Entity({
  name: PageTitles.knock,
  language: LanguagePage.knock,
})
export class Knock implements IKnock {
  @Column({
    type: ColumnType.string,
    isEditable: true,
  })
  comments: string;

  @Column({
    type: ColumnType.string,
  })
  vendorSalesRep: string;

  @Column({
    type: ColumnType.link,
    table: {
      defaultPosition: 3,
    },
  })
  addressLink?: Link;

  @Column({
    type: ColumnType.option,
    isEditable: true,
    table: {
      defaultPosition: 1,
    },
  })
  knockStatus: string;

  @Column({
    type: ColumnType.option,
    isEditable: true,
    table: {
      defaultPosition: 2,
    },
  })
  knockType: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 4,
    },
  })
  createdAt: Date;

  @Column({
    type: ColumnType.link,
    table: {
      defaultPosition: 5,
    },
  })
  createdByLink?: Link;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 6,
    },
  })
  modifiedAt?: Date;

  @Column({
    type: ColumnType.link,
    table: {
      defaultPosition: 7,
    },
  })
  modifiedByLink?: Link;

  id: ID;
  babylonInteraction: string;
  comebackDate: Date;
  comebackTime: string;
  contractExpiryDate: Date;
  currentInternetProvider: string;
  currentInternetSpeed: string;
  currentMobilityProvider: string;
  endGeolocation: string;
  followUpDate: Date;
  is6OrMoreSuitesInBuilding: boolean;
  isBuildingInStrata: boolean;
  isThereMainElectricalRoom: boolean;
  isUnderConstruction: boolean;
  languagePreference: string;
  offerExpiryDate: Date;
  orderId: string;
  orderingSystem: string;
  osm: string;
  otherReason: string;
  reasonForNo: string;
  salesActivity: string;
  startGeolocation: string;
  storeCode: string;

  href?: string;
  createdBy: string;
  modifiedBy?: string;
  addressId: string;

  createdByData?: VendorSalesRep;
  modifiedByData?: VendorSalesRep;
  salesRepData?: VendorSalesRep;
  salesRepLink?: Link;

  constructor(
    drop: IKnock = {
      addressId: '',
      id: '',
      knockStatus: '',
      knockType: '',
      comments: '',
      createdBy: '',
      createdAt: new Date(),
      modifiedBy: '',
      modifiedAt: new Date(),
      babylonInteraction: '',
      comebackDate: new Date(),
      comebackTime: '',
      contractExpiryDate: new Date(),
      currentInternetProvider: '',
      currentInternetSpeed: '',
      currentMobilityProvider: '',
      endGeolocation: '',
      followUpDate: new Date(),
      is6OrMoreSuitesInBuilding: false,
      isBuildingInStrata: false,
      isThereMainElectricalRoom: false,
      isUnderConstruction: false,
      languagePreference: '',
      offerExpiryDate: new Date(),
      orderId: '',
      orderingSystem: '',
      osm: '',
      otherReason: '',
      reasonForNo: '',
      salesActivity: '',
      startGeolocation: '',
      storeCode: '',
      vendorSalesRep: '',
      href: '',
    },
    extraInformation?: IExtraKnockInformation
  ) {
    const {
      addressId,
      id,
      knockStatus,
      knockType,
      comments,
      createdBy,
      createdAt,
      modifiedBy,
      modifiedAt,
      babylonInteraction,
      comebackDate,
      comebackTime,
      contractExpiryDate,
      currentInternetProvider,
      currentInternetSpeed,
      currentMobilityProvider,
      endGeolocation,
      followUpDate,
      is6OrMoreSuitesInBuilding,
      isBuildingInStrata,
      isThereMainElectricalRoom,
      isUnderConstruction,
      languagePreference,
      offerExpiryDate,
      orderId,
      orderingSystem,
      osm,
      otherReason,
      reasonForNo,
      salesActivity,
      startGeolocation,
      storeCode,
      vendorSalesRep,
      href,
    } = drop;

    this.id = id;
    this.addressId = addressId;
    this.knockStatus = knockStatus;
    this.knockType = knockType;
    this.comments = comments;
    this.createdBy = createdBy;
    this.createdAt = createdAt;
    this.modifiedBy = modifiedBy;
    this.modifiedAt = modifiedAt;

    this.babylonInteraction = babylonInteraction;
    this.comebackDate = comebackDate;
    this.comebackTime = comebackTime;
    this.contractExpiryDate = contractExpiryDate;
    this.currentInternetProvider = currentInternetProvider;
    this.currentInternetSpeed = currentInternetSpeed;
    this.currentMobilityProvider = currentMobilityProvider;
    this.endGeolocation = endGeolocation;
    this.followUpDate = followUpDate;
    this.is6OrMoreSuitesInBuilding = is6OrMoreSuitesInBuilding;
    this.isBuildingInStrata = isBuildingInStrata;
    this.isThereMainElectricalRoom = isThereMainElectricalRoom;
    this.isUnderConstruction = isUnderConstruction;
    this.languagePreference = languagePreference;
    this.offerExpiryDate = offerExpiryDate;
    this.orderId = orderId;
    this.orderingSystem = orderingSystem;
    this.osm = osm;
    this.otherReason = otherReason;
    this.reasonForNo = reasonForNo;
    this.salesActivity = salesActivity;
    this.startGeolocation = startGeolocation;
    this.storeCode = storeCode;
    this.vendorSalesRep = vendorSalesRep;
    this.href = href;

    /*
      Computer properties
     */

    this.addressLink = {
      link: this.addressId
        ? `/app/address360/detail/${this.addressId}`
        : undefined,
      text: this.addressId,
    };

    if (extraInformation?.createdByData) {
      this.createdByData = extraInformation.createdByData;
    }

    if (extraInformation?.modifiedByData) {
      this.modifiedByData = extraInformation.modifiedByData;
    }

    if (extraInformation?.salesRepData) {
      this.salesRepData = extraInformation.salesRepData;
    }

    if (this.knockStatus === 'Successful Order') {
      this.comments = '';
    }

    this.createdByLink = Knock.isTelusMember(this.createdBy)
      ? {
          link: undefined,
          text: this.createdBy,
        }
      : {
          link: this.createdByData
            ? `/app/vendor/sales-rep/detail/${this.createdByData.id}`
            : undefined,
          text: this.createdByData ? this.createdByData.name : this.createdBy,
        };

    this.modifiedByLink = Knock.isTelusMember(this.modifiedBy)
      ? {
          link: undefined,
          text: this.modifiedBy,
        }
      : {
          link: this.modifiedByData
            ? `/app/vendor/sales-rep/detail/${this.modifiedByData.id}`
            : undefined,
          text: this.modifiedByData
            ? this.modifiedByData.name
            : this.modifiedBy,
        };

    this.salesRepLink =
      this.vendorSalesRep && this.vendorSalesRep !== 'string'
        ? {
            link: this.salesRepData
              ? `/app/vendor/sales-rep/detail/${this.salesRepData.id}`
              : undefined,
            text: this.salesRepData
              ? this.salesRepData.name
              : this.vendorSalesRep,
          }
        : {
            link: undefined,
            text: this.vendorSalesRep,
          };
  }

  public static isTelusMember(id) {
    if (!id) {
      return undefined;
    }

    const firstCharacterUserName = id[0];
    return (
      firstCharacterUserName.toLowerCase() === 't' ||
      firstCharacterUserName.toLowerCase() === 'x' ||
      id.toLowerCase() === 'informatica'
    );
  }
}

@Entity({
  name: PageTitles.knockDetails,
})
export class KnockDetails {
  id: ID;

  @Column({
    type: ColumnType.string,
  })
  babylonInteraction: string;

  @Column({
    type: ColumnType.date,
  })
  comebackDate: Date;

  @Column({
    type: ColumnType.string,
  })
  comebackTime: string;

  @Column({
    type: ColumnType.date,
  })
  contractExpiryDate: Date;

  @Column({
    type: ColumnType.string,
  })
  currentInternetSpeed: string;

  @Column({
    type: ColumnType.string,
  })
  endGeolocation: string;

  @Column({
    type: ColumnType.date,
  })
  followUpDate: Date;

  @Column({
    type: ColumnType.boolean,
  })
  is6OrMoreSuitesInBuilding: boolean;

  @Column({
    type: ColumnType.boolean,
  })
  isBuildingInStrata: boolean;

  @Column({
    type: ColumnType.boolean,
  })
  isThereMainElectricalRoom: boolean;

  @Column({
    type: ColumnType.boolean,
  })
  isUnderConstruction: boolean;

  @Column({
    type: ColumnType.string,
  })
  languagePreference: string;

  @Column({
    type: ColumnType.date,
  })
  offerExpiryDate: Date;

  @Column({
    type: ColumnType.string,
  })
  orderId: string;

  @Column({
    type: ColumnType.string,
  })
  orderingSystem: string;

  @Column({
    type: ColumnType.string,
  })
  osm: string;

  @Column({
    type: ColumnType.string,
  })
  otherReason: string;

  @Column({
    type: ColumnType.string,
  })
  reasonForNo: string;

  @Column({
    type: ColumnType.string,
  })
  salesActivity: string;

  @Column({
    type: ColumnType.string,
  })
  startGeolocation: string;

  @Column({
    type: ColumnType.string,
  })
  storeCode: string;

  @Column({
    type: ColumnType.string,
  })
  vendorSalesRep: string;
}
